import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { BackButton } from '@/components/BackButton';
import { 
  BookOpen, 
  FileText, 
  Video, 
  MessageSquare,
  ArrowRight,
  CheckCircle,
  Lock,
  AlertTriangle,
  Crown
} from 'lucide-react';

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  
  // التحقق إذا كان المستخدم مشترك
  const isSubscribed = user?.subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';

  const getStatusBadge = () => {
    if (!user) return null;
    
    const role = user.role || 'user';
    const roleText = t(`dashboard_${role}`) || role;
    
    const roleColors = {
      super_admin: 'bg-gradient-to-r from-red-500 to-orange-500',
      owner: 'bg-gradient-to-r from-purple-500 to-pink-500',
      admin: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      user: 'bg-gradient-to-r from-emerald-500 to-teal-500'
    };

    return (
      <Badge className={`${roleColors[role] || roleColors.user} text-white border-0 px-2 py-0.5 text-xs`}>
        {roleText}
      </Badge>
    );
  };

  const quickActions = [
    {
      icon: FileText,
      title: t('dashboard_tests_title'),
      description: t('dashboard_tests_desc'),
      path: '/tests',
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-50',
      emoji: '📝',
      isFree: true
    },
    {
      icon: BookOpen,
      title: t('dashboard_lessons_title'),
      description: t('dashboard_lessons_desc'),
      path: '/lessons',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      emoji: '📚'
    },
    {
      icon: AlertTriangle,
      title: t('ui.trafficSigns'),
      description: t('ui.learnTrafficSigns'),
      path: '/traffic-signs',
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      emoji: '🚦'
    },
    {
      icon: Video,
      title: t('ui.quickTips'),
      description: t('ui.goldenTips'),
      path: '/quick-tips',
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      emoji: '💡'
    },
    {
      icon: MessageSquare,
      title: t('dashboard_assistant_title'),
      description: t('dashboard_assistant_desc'),
      path: '/assistant',
      color: 'from-rose-500 to-red-500',
      bgColor: 'bg-rose-50',
      emoji: '🤖'
    }
  ];

  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  
  // دالة للتعامل مع النقر على البطاقات
  const handleCardClick = (path, isFree) => {
    // صفحة الاختبارات مجانية جزئياً
    if (path === '/tests' || isFree) {
      navigate(path);
      return;
    }
    
    if (isSubscribed) {
      navigate(path);
    } else {
      setShowSubscribeModal(true);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" dir={isRTL ? 'rtl' : 'ltr'} data-testid="dashboard-page">
      <Navbar />
      
      <main className="flex-grow flex flex-col container mx-auto px-3 sm:px-4 py-4 sm:py-8 mt-16 sm:mt-20">
        <BackButton to="/" className="mb-2 sm:mb-3" />
        
        {/* Compact Welcome Header - No margin bottom */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-t-2xl sm:rounded-t-3xl p-4 sm:p-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
          <div className="relative z-10">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h1 className="text-lg sm:text-2xl font-bold mb-1" data-testid="welcome-title">
                  {t('dashboard_welcome')}, {user?.name?.split(' ')[0]}! 👋
                </h1>
                <div className="flex items-center gap-2 flex-wrap">
                  {getStatusBadge()}
                  {isSubscribed ? (
                    <span className="text-emerald-400 text-xs flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      {t('ui.subscribed')}
                    </span>
                  ) : (
                    <span className="text-amber-400 text-xs flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      {t('ui.trial')}
                    </span>
                  )}
                </div>
              </div>
              {!isSubscribed && (
                <Button
                  onClick={() => navigate('/priser')}
                  size="sm"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full text-xs px-3 py-1 h-auto"
                >
                  <Crown className="w-3 h-3 mr-1" />
                  {t('ui.subscribe')}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions - Connected to header */}
        <div className="bg-white rounded-b-2xl sm:rounded-b-3xl p-4 sm:p-6 shadow-lg">
          <h2 className="text-sm sm:text-base font-bold text-slate-900 mb-3">
            {t('dashboard_quick_actions')} ⚡
          </h2>
          
          {/* Old style cards but compact */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              const needsSubscription = !action.isFree && !isSubscribed;
              
              return (
                <Card
                  key={index}
                  className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border-2 border-slate-100 hover:border-emerald-400"
                  onClick={() => handleCardClick(action.path, action.isFree)}
                  data-testid={`dashboard-card-${action.path.replace('/', '')}`}
                >
                  <div className="p-3 sm:p-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform bg-gradient-to-r ${action.color}`}>
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-bold text-slate-900 text-xs sm:text-sm mb-1 line-clamp-1">
                      {action.title}
                    </h3>
                    <p className="text-slate-500 text-[10px] sm:text-xs leading-tight mb-2 line-clamp-2 hidden sm:block">
                      {action.description}
                    </p>
                    <div className="flex items-center justify-between">
                      {action.isFree ? (
                        <span className="text-[10px] sm:text-xs text-emerald-600 font-medium">
                          {t('ui.free')}
                        </span>
                      ) : needsSubscription ? (
                        <span className="text-[10px] sm:text-xs text-amber-600 font-medium flex items-center gap-0.5">
                          <Lock className="w-2.5 h-2.5" />
                        </span>
                      ) : (
                        <span className="text-[10px] sm:text-xs text-emerald-600">
                          <CheckCircle className="w-2.5 h-2.5" />
                        </span>
                      )}
                      <ArrowRight className={`w-3 h-3 sm:w-4 sm:h-4 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all ${isRTL ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Spacer to push CTA banner to bottom */}
        <div className="flex-grow"></div>

        {/* CTA Banner - At the bottom near footer */}
        <div className="relative h-32 sm:h-48 rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg mt-6">
          <img 
            src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1200&h=400&fit=crop" 
            alt="Övningskörning - DriveMaster körkortsteori"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/50 flex items-center">
            <div className="p-4 sm:p-6">
              <h2 className="text-lg sm:text-2xl font-bold text-white mb-1 sm:mb-2">
                {t('dashboard_continue_learning')} 🚗
              </h2>
              <p className="text-xs sm:text-sm text-slate-200 mb-3 sm:mb-4 max-w-sm line-clamp-2">
                {t('dashboard_subtitle')}
              </p>
              <Button
                onClick={() => navigate('/teoriprov')}
                size="sm"
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-4 sm:px-6 py-2 text-xs sm:text-sm font-bold shadow-lg"
              >
                {t('ui.startTest')}
                <ArrowRight className={`w-3 h-3 sm:w-4 sm:h-4 ms-1 ${isRTL ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* مودال الاشتراك */}
      {showSubscribeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="max-w-sm w-full bg-white rounded-2xl shadow-2xl p-6 text-center animate-in fade-in zoom-in duration-300 relative">
            <button 
              onClick={() => setShowSubscribeModal(false)}
              className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors text-sm"
              data-testid="close-subscribe-modal"
            >
              ✕
            </button>
            
            <div className="text-5xl mb-4">🔐</div>
            
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {t('ui.forSubscribers')}
            </h2>
            
            <p className="text-slate-600 text-sm mb-5">
              {t('ui.subscribeForAccess')}
            </p>
            
            <div className="space-y-2">
              <button
                onClick={() => navigate('/priser')}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold rounded-xl shadow-lg text-sm"
                data-testid="subscribe-now-btn"
              >
                {t('ui.subscribeNow')} 🚀
              </button>
              
              <button
                onClick={() => setShowSubscribeModal(false)}
                className="w-full py-2.5 border-2 border-slate-200 text-slate-600 font-medium rounded-xl hover:bg-slate-50 text-sm"
                data-testid="cancel-subscribe-btn"
              >
                {t('ui.later')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
